'use client';

import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import { useAnalyticsUtilities } from '@air/analytics';
import { type Account, type FullWorkspace } from '@air/api/types';
import { useLaunchDarklyContext, useWorkspace } from '@air/plugin-ui';
import { useAccount } from '@air/utils-auth';
import { Bugsnag } from '@air/utils-error';

import { useURLWorkspaceId } from '~/hooks/url/useURLWorkspaceId';

export const useConfigureThirdPartyServices = () => {
  const selectedWorkspaceId = useURLWorkspaceId();
  const { data: workspace } = useWorkspace({ workspaceId: selectedWorkspaceId ?? undefined });
  const { data: account } = useAccount();
  const {
    identify: launchDarklyIdentify,
    isReady: isLaunchDarklyReady,
    hasIdentified: hasLaunchDarklyIdentified,
  } = useLaunchDarklyContext();
  const { identify: analyticsIdentify, setGlobalEventProps } = useAnalyticsUtilities();

  const workspaceRef = useRef<FullWorkspace>();
  const accountRef = useRef<Account>();
  // Must be state, not a ref: a ref wouldn't re-render, so a no-op identify would leave LaunchDarkly
  // reporting ready while this stays unset, and the access check below would never resolve.
  const [identifiedWorkspaceId, setIdentifiedWorkspaceId] = useState<string>();

  useEffect(() => {
    const isWorkspaceChanged = workspace && (!workspaceRef.current || !isEqual(workspaceRef.current, workspace));
    const isAccountChanged = account && (!accountRef.current || !isEqual(accountRef.current, account));

    if (workspace && account && (isWorkspaceChanged || isAccountChanged)) {
      workspaceRef.current = workspace;
      accountRef.current = account;
      setIdentifiedWorkspaceId(workspace.id);

      Bugsnag.setUser(account.authId ?? account.id, account.email, `${account.firstName} ${account.lastName}`);

      launchDarklyIdentify({
        anonymous: false,
        email: account.email,
        firstName: account.firstName,
        key: account.authId ?? account.id,
        lastName: account.lastName,
        platform: 'shopify',
        workspaceId: workspace.id,
        workspaceName: workspace.name,
        workspacePlan: workspace.plan,
      });

      analyticsIdentify(account.authId ?? account.id, {
        email: account.email,
        first_name: account.firstName,
        last_name: account.lastName,
      });

      setGlobalEventProps({
        airAccountId: account.authId ?? account.id,
        app_name: 'ShopifyPlugin',
        userEmail: account.email,
        workspaceId: workspace.id,
        workspaceName: workspace.name,
      });
    }
  }, [workspace, account, launchDarklyIdentify, analyticsIdentify, setGlobalEventProps]);

  const isCurrentWorkspace = identifiedWorkspaceId !== undefined && identifiedWorkspaceId === selectedWorkspaceId;

  return {
    hasLaunchDarklyIdentified: hasLaunchDarklyIdentified && isCurrentWorkspace,
    isLaunchDarklyReady: isLaunchDarklyReady && isCurrentWorkspace,
  };
};
